import type { FC } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import { useSkipInitialAnimation } from '@apps/guest/packages/layout-engine/hooks/useSkipInitialAnimation';
import { useDrawCroppedImageToCanvas } from '@shared/hooks/useDrawCroppedImageToCanvas';
import { useProgressiveImage } from './useProgressiveImage';
import { addSuppressedError } from '@shared/utils/logger';
import { scaleSizesExpression } from './ssrHeroSizes';
import { AnimatedPhotoBox } from './OptimizedEventGuestSitePhoto.styles';
import type { OptimizedEventGuestSitePhotoProps } from './OptimizedEventGuestSitePhoto.types';

// Small delay so the high-res image has time to decode/paint before the low-res
// starts fading out. Without this, the low-res opacity transition begins on the
// same frame the high-res src is set, causing a brief flicker.
const DEFAULT_CROSSFADE_DELAY_MS = 150;
const buildCrossfadeTransition = (delayMs: number) => animationTransition('opacity', { delay: `${delayMs}ms` });
// When the high-res is served from disk cache, skip the transition entirely.
const INSTANT_TRANSITION = 'opacity 0s';

// Blur effect on the low-res (tiny) layer to mimic blur-hash behavior.
// The tiny rendition is very low resolution — blurring it creates a smooth
// placeholder that sharpens when the high-res layer fades in.
// scale(1.05) extends the blurred layer slightly beyond the container to hide
// the transparent edge artifacts that CSS blur creates at boundaries.
// 5% is enough to cover the 20px blur radius on typical container sizes
// without causing noticeable layout shift during the crossfade.
const LOW_RES_BLUR_FILTER = 'blur(20px)';
const LOW_RES_BLUR_SCALE = 'scale(1.05)';

// ---------------------------------------------------------------------------
// Cropped: fixed-aspect containers (PhotoTrio, PhotoDuo, PhotoArch, etc.)
//
// Uses an <img> element inside an overflow:hidden container. The crop region
// is replicated via CSS transforms derived from the PercentCrop values:
//
//   width:  10000 / cropWidth  %   → scales the image so the crop region fills the container width
//   height: 10000 / cropHeight %   → same for height
//   left:  -(x / cropWidth) * 100% → shifts the image left so the crop region starts at x=0
//   top:   -(y / cropHeight) * 100% → same for vertical offset
//
// Both dimensions use percentages relative to the container, so the crop
// renders correctly at any container size without JS measurement.
// ---------------------------------------------------------------------------
/** The attribute handoff both self-loading sharp modes (eager + lazy) pass to their render
 *  branch: final URLs plus the ref/onLoad pair through which the on-page <img> reports its own
 *  load back to the component (no offscreen preloader runs in either mode). */
type SharpImageHandoff = Readonly<{
  src: string;
  srcSet?: string;
  sizes?: string;
  imgRef: React.RefObject<HTMLImageElement>;
  onLoad: () => void;
}>;

/** Eager-SSR mode: sharp layer attributes render unconditionally (not gated on
 *  isHighResLoaded) so the browser fetches at parse time, with fetchpriority="high". */
type EagerSharp = SharpImageHandoff;

const CroppedFixedAspect: FC<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  isHighResFromCache: boolean;
  cropInfo: NonNullable<OptimizedEventGuestSitePhotoProps['cropInfo']>;
  onHighResError: () => void;
  crossfadeTransition: string;
  highResSrcSet?: string;
  highResSizes?: string;
  eagerSharp?: EagerSharp;
}> = ({ lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, cropInfo, onHighResError, crossfadeTransition, highResSrcSet, highResSizes, eagerSharp }) => {
  const { x, y, width: cw, height: ch } = cropInfo;
  const imgStyle = {
    width: `${10000 / cw}%`,
    height: `${10000 / ch}%`,
    left: `${-(x / cw) * 100}%`,
    top: `${-(y / ch) * 100}%`,
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
    >
      {/* High-res layer — always in DOM so it's pre-mounted and ready to paint.
          src/srcSet/sizes are only set when useProgressiveImage reports the image is cached,
          preventing the browser from eagerly fetching xlarge (or a srcSet candidate) in
          parallel with small. srcSet/sizes let the browser pick a rendition sized
          to this container's actual measured width instead of always downloading `xlarge`. In
          eager-SSR mode, eagerSharp supplies these attributes unconditionally so the
          browser starts fetching the sharp candidate from the SSR markup, before hydration —
          rendered as a plain <img> rather than `Box as="img"` because Box's styled-components
          prop filter (@emotion/is-prop-valid) doesn't recognize `fetchpriority` and silently
          drops it, which would defeat the whole point of this mode.
          This component has no notion of "am I the only photo on the page" — that's a call-site
          decision. Multi-photo layouts (duo, trio) deliberately let every slot request
          eagerSharp/fetchpriority=high, because every slot renders above the fold and any one of
          them could end up being the actual LCP element. Sliders are the one exception: fade
          mode keeps every slide mounted (see BrannanPhotoSlider), so the call site there scopes
          ssrSizes to just the first/visible slide instead of relying on this component to
          differentiate. */}
      {eagerSharp ? (
        <img
          ref={eagerSharp.imgRef}
          src={eagerSharp.src}
          srcSet={eagerSharp.srcSet}
          sizes={eagerSharp.sizes}
          alt=""
          onError={onHighResError}
          onLoad={eagerSharp.onLoad}
          style={{ ...imgStyle, position: 'absolute', zIndex: 1 }}
          // fetchpriority is not yet in @types/react (added in React 18.3) but React 17 passes
          // through unknown lowercase HTML attributes to the DOM — same technique as PreloadImages.tsx.
          {...({ fetchpriority: 'high' } as {})}
        />
      ) : (
        <Box
          as="img"
          src={isHighResLoaded ? highResUrl : undefined}
          srcSet={isHighResLoaded ? highResSrcSet : undefined}
          sizes={isHighResLoaded ? highResSizes : undefined}
          alt=""
          position="absolute"
          zIndex={1}
          onError={onHighResError}
          style={imgStyle}
        />
      )}
      {/* Low-res layer — stacks above high-res (z-index 2 > 1), fades out to
          reveal the sharp high-res underneath once it's loaded and painted. */}
      <Box
        as="img"
        src={lowResUrl}
        alt=""
        position="absolute"
        zIndex={2}
        style={{
          ...imgStyle,
          filter: LOW_RES_BLUR_FILTER,
          transform: LOW_RES_BLUR_SCALE,
          opacity: isHighResLoaded ? 0 : 1,
          transition: isHighResFromCache ? INSTANT_TRANSITION : crossfadeTransition,
        }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Cropped: square containers (Aloha slider)
//
// Same <img> crop math as CroppedFixedAspect, but wrapped in a square
// intermediate div (aspect-ratio: 1) centered via translate(-50%, -50%).
// The square container ensures percentage-based crop transforms resolve
// equally in both dimensions — without it, a rectangular container would
// stretch the crop positioning unevenly.
// ---------------------------------------------------------------------------
const CroppedSquare: FC<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  isHighResFromCache: boolean;
  cropInfo: NonNullable<OptimizedEventGuestSitePhotoProps['cropInfo']>;
  onHighResError: () => void;
  crossfadeTransition: string;
  highResSrcSet?: string;
  highResSizes?: string;
  eagerSharp?: EagerSharp;
}> = ({ lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, cropInfo, onHighResError, crossfadeTransition, highResSrcSet, highResSizes, eagerSharp }) => {
  const { x, y, width: cw, height: ch } = cropInfo;
  const imgStyle = {
    width: `${10000 / cw}%`,
    height: `${10000 / ch}%`,
    left: `${-(x / cw) * 100}%`,
    top: `${-(y / ch) * 100}%`,
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
    >
      {/* Square intermediate div — min-width/min-height: 100% ensures it covers
          the container regardless of aspect ratio. Centered with translate. */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        minWidth="100%"
        minHeight="100%"
        overflow="hidden"
        style={{ aspectRatio: '1', transform: 'translate(-50%, -50%)' }}
      >
        {/* srcSet/sizes and eager-SSR mode: see CroppedFixedAspect's
            high-res layer comment above (incl. why eager mode renders a plain <img>). */}
        {eagerSharp ? (
          <img
            ref={eagerSharp.imgRef}
            src={eagerSharp.src}
            srcSet={eagerSharp.srcSet}
            sizes={eagerSharp.sizes}
            alt=""
            onError={onHighResError}
            onLoad={eagerSharp.onLoad}
            style={{ ...imgStyle, position: 'absolute', zIndex: 1 }}
            // fetchpriority is not yet in @types/react (added in React 18.3) but React 17 passes
            // through unknown lowercase HTML attributes to the DOM — same technique as PreloadImages.tsx.
            {...({ fetchpriority: 'high' } as {})}
          />
        ) : (
          <Box
            as="img"
            src={isHighResLoaded ? highResUrl : undefined}
            srcSet={isHighResLoaded ? highResSrcSet : undefined}
            sizes={isHighResLoaded ? highResSizes : undefined}
            alt=""
            position="absolute"
            zIndex={1}
            onError={onHighResError}
            style={imgStyle}
          />
        )}
        <Box
          as="img"
          src={lowResUrl}
          alt=""
          position="absolute"
          zIndex={2}
          style={{
            ...imgStyle,
            filter: LOW_RES_BLUR_FILTER,
            transform: LOW_RES_BLUR_SCALE,
            opacity: isHighResLoaded ? 0 : 1,
            transition: isHighResFromCache ? INSTANT_TRANSITION : crossfadeTransition,
          }}
        />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Cropped: viewport containers (PhotoBackground)
//
// Uses background-image with background-size: cover instead of <img>.
// An intermediate div is sized by the container's aspect ratio (not the
// crop's) with min-width/min-height: 100% — this replicates the canvas
// "bestSuitable" sizing where the image always covers the viewport.
//
// background-position centers on the crop region:
//   x + cropWidth/2  → horizontal center of the crop as a percentage
//   y + cropHeight/2 → vertical center of the crop as a percentage
//
// At wide viewports the intermediate div grows wider → the image "blooms"
// (zooms in) rather than revealing more of the photo outside the crop.
// ---------------------------------------------------------------------------
const CroppedViewport: FC<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  isHighResFromCache: boolean;
  cropInfo: NonNullable<OptimizedEventGuestSitePhotoProps['cropInfo']>;
  containerAspectRatio: number;
  crossfadeTransition: string;
}> = ({ lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, cropInfo, containerAspectRatio, crossfadeTransition }) => {
  const { x, y, width: cw, height: ch } = cropInfo;
  const bgPosition = `${x + cw / 2}% ${y + ch / 2}%`;

  return (
    <Box
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
    >
      <Box
        position="absolute"
        top="50%"
        left="50%"
        minWidth="100%"
        minHeight="100%"
        style={{
          aspectRatio: `${containerAspectRatio}`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1}
          style={{
            backgroundImage: isHighResLoaded ? `url("${highResUrl}")` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: bgPosition,
            backgroundRepeat: 'no-repeat',
          }}
        />
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={2}
          style={{
            backgroundImage: `url("${lowResUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: bgPosition,
            backgroundRepeat: 'no-repeat',
            filter: LOW_RES_BLUR_FILTER,
            transform: LOW_RES_BLUR_SCALE,
            opacity: isHighResLoaded ? 0 : 1,
            transition: isHighResFromCache ? INSTANT_TRANSITION : crossfadeTransition,
          }}
        />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Uncropped: all container types
//
// Two stacked <img> layers (sharp under blur) with object-fit/object-position
// standing in for the former background-size/background-position divs — same
// geometry, but a real <img> can carry srcSet/sizes (responsive candidates)
// and native loading="lazy" (PRNT-5084). No crop math needed.
// ---------------------------------------------------------------------------
/** Lazy mode: sharp layer attributes render unconditionally with loading="lazy", so the browser
 *  defers the fetch until near-viewport — from SSR markup, before hydration. Same self-load
 *  ownership pattern as EagerSharp, opposite fetch-priority intent. */
type LazySharp = SharpImageHandoff;

const UncroppedPhoto: FC<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  isHighResFromCache: boolean;
  backgroundPosition?: string;
  objectFit: 'cover' | 'contain';
  crossfadeTransition: string;
  highResSrcSet?: string;
  highResSizes?: string;
  lazySharp?: LazySharp;
}> = ({ lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, backgroundPosition = 'center', objectFit, crossfadeTransition, highResSrcSet, highResSizes, lazySharp }) => {
  // Shared geometry for both layers — fills the container exactly like the former
  // absolutely-positioned background divs.
  const layerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit,
    objectPosition: backgroundPosition,
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
    >
      {/* Sharp layer. Plain <img>, not Box as="img" — Box's styled-components prop filter
          (@emotion/is-prop-valid) silently drops loading/decoding, same as fetchpriority in the
          eager-SSR branches above. Lazy mode ships src/srcSet/sizes in the markup so the
          browser's own lazy-loader owns the fetch; default mode keeps the legacy hydration-gated
          timing (attributes appear only once the offscreen preload reports loaded). */}
      {lazySharp ? (
        <img
          ref={lazySharp.imgRef}
          src={lazySharp.src}
          srcSet={lazySharp.srcSet}
          sizes={lazySharp.sizes}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={lazySharp.onLoad}
          // skipPreload bypasses useProgressiveImage's onerror logging, so this img must report
          // CDN failures itself or they go dark in New Relic. Loaded state stays false, so the
          // blurred placeholder remains — same graceful degradation as the preloader path.
          onError={() => {
            addSuppressedError(new Error('Lazy sharp image load failed'), {
              context: 'UncroppedPhoto',
              highResUrl,
            });
          }}
          style={{ ...layerStyle, zIndex: 1 }}
        />
      ) : (
        <img
          src={isHighResLoaded ? highResUrl : undefined}
          srcSet={isHighResLoaded ? highResSrcSet : undefined}
          sizes={isHighResLoaded ? highResSizes : undefined}
          alt=""
          style={{ ...layerStyle, zIndex: 1 }}
        />
      )}
      {/* Blur layer — stacks above the sharp layer (z-index 2 > 1) and fades out once the sharp
          image has loaded, exactly like the cropped branches. Renders nothing when there is no
          low-res URL (parity with the former `backgroundImage: undefined`). */}
      {lowResUrl ? (
        <img
          src={lowResUrl}
          alt=""
          // Deliberately NOT lazy, even when the sharp layer is: a placeholder is only useful if
          // it arrives FIRST, and sharing the sharp layer's lazy threshold starts both fetches at
          // the same moment — on a warm cache or fast connection they then complete in the same
          // frame, so this layer never paints and the guest sees an empty reserved box instead of
          // a blurred preview (measured on /kkplusrt). A tiny rendition is ~3KB, so buying the
          // head start costs ~21KB across a full single-page layout. Do not "optimize" this to
          // loading="lazy".
          style={{
            ...layerStyle,
            zIndex: 2,
            // Full-box neutral fill only for 'cover', where the image fills the box so the fill is
            // fully covered. For 'contain' the blurred low-res IS the placeholder and letterboxes
            // exactly like the high-res; a full-box fill would be larger than the (possibly
            // maxHeight-capped) photo and read as a placeholder that doesn't match the photo.
            backgroundColor: objectFit === 'cover' ? 'lightgray' : undefined,
            filter: LOW_RES_BLUR_FILTER,
            transform: LOW_RES_BLUR_SCALE,
            opacity: isHighResLoaded ? 0 : 1,
            transition: isHighResFromCache ? INSTANT_TRANSITION : crossfadeTransition,
          }}
        />
      ) : null}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Canvas fallback — used when CSS <img> crop fails (onError). Falls back to
// useDrawCroppedImageToCanvas which draws the cropped region to a <canvas>.
// This is a safety net, not the primary rendering path.
// ---------------------------------------------------------------------------
const CanvasFallback: FC<{
  imageUrl: string;
  cropInfo: NonNullable<OptimizedEventGuestSitePhotoProps['cropInfo']>;
  width: number;
  height: number;
}> = ({ imageUrl, cropInfo, width, height }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useDrawCroppedImageToCanvas({ canvasRef, cropInfo, height, width, imageUrl });

  return (
    <Box
      width={width}
      height={height}
      position="relative"
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
//
// Orchestrates progressive loading, animation, and rendering strategy.
// Internally calls useSkipInitialAnimation (SSR fade-in skip) and
// useProgressiveImage (small → xlarge crossfade), so consumers don't need to.
//
// `isolation: isolate` creates a stacking context so the internal z-indexes
// (used for the two-layer crossfade) don't leak into the parent and interfere
// with sibling elements like decorative overlays (e.g. ArchBorderOverlay).
// ---------------------------------------------------------------------------
export const OptimizedEventGuestSitePhoto: FC<OptimizedEventGuestSitePhotoProps> = props => {
  const {
    photoUrl,
    cropInfo,
    width,
    height,
    containerType,
    containerAspectRatio,
    lowResRendition,
    highResRendition,
    backgroundPosition,
    objectFit = 'cover',
    crossfadeDelayMs = DEFAULT_CROSSFADE_DELAY_MS,
    skipRendition = false,
    preloadCrossOrigin = true,
  } = props;

  const skipAnimation = useSkipInitialAnimation();
  // Real-<img> branches benefit from srcSet/sizes: the two cropped ones (fixed-aspect/square) and
  // the uncropped branch (PRNT-5084 converted it from background divs to <img>s). Only the
  // CSS-background CroppedViewport branch can't act on them, so containerWidthPx stays undefined
  // there and useProgressiveImage falls back to its original single-URL behavior.
  const isImgBasedCropped = !!cropInfo && (containerType === 'fixed-aspect' || containerType === 'square');
  const isUncropped = !cropInfo;
  // The crop renderer scales the <img> up to `10000 / cropInfo.width` percent of the container
  // (see CroppedFixedAspect/CroppedSquare below) — sizes must reflect that same scale-up, or the
  // browser picks a candidate sized for the container instead of the actually-rendered image.
  // For 'square', the intermediate box grows to cover the larger of width/height (min-width/
  // min-height: 100% + aspect-ratio: 1), so a non-square container's base is that larger side,
  // not the raw width.
  const baseWidthPx = containerType === 'square' ? Math.max(width, height) : width;
  // Single source of truth for the crop scale-up: CroppedFixedAspect/CroppedSquare's imgStyle
  // width (`10000 / cropInfo.width` percent) and effectiveContainerWidthPx below must derive from
  // the SAME factor as the eager static sizes estimate (cropScaleFactor, used further down) or
  // the three drift apart by construction. `100 / cropInfo.width` is that factor expressed as a
  // multiplier instead of a percent.
  const cropScaleFactor = cropInfo ? 100 / cropInfo.width : 1;
  // Eager-SSR mode: only the two real-<img> branches qualify, and only once the
  // initial-animation skip is active (SSR and the very first client render agree) — a later
  // client-side navigation re-mount must fall back to the hydration-gated path, since by then
  // there's no SSR markup to have gotten ahead of.
  const isEagerSharpImage = skipAnimation && !!props.ssrSizes && isImgBasedCropped && !skipRendition;
  // Lazy mode (uncropped page photos, PRNT-5084): the sharp <img> renders loading="lazy" with its
  // URLs in markup, so the browser owns fetch timing — no eligibility latch needed (unlike eager
  // mode, laziness is correct on SSR, hydration, and client-side navigation alike).
  const isLazySharpImage = isUncropped && !!props.lazySharpImg;

  // The uncropped branch gets a width-based candidate list ONLY in lazy mode. Non-opt-in
  // uncropped call sites (incl. above-fold header photos via PhotoBackgroundRenderer) must keep
  // fetching exactly the resource the background-image path fetched — the single xlarge
  // rendition — so their srcSet/sizes stay off. (Uncropped renders the <img> at exactly the
  // container's size — object-fit, no crop scale-up — so the raw width prop is the estimate.)
  const effectiveContainerWidthPx = isImgBasedCropped ? baseWidthPx * cropScaleFactor : isLazySharpImage ? width : undefined;

  const { lowResUrl, highResUrl, isHighResLoaded, isHighResFromCache, highResSrcSet, highResSizes } = useProgressiveImage({
    photoUrl,
    lowResRendition,
    highResRendition,
    skipRendition,
    preloadCrossOrigin,
    containerWidthPx: effectiveContainerWidthPx,
    skipPreload: isEagerSharpImage || isLazySharpImage,
  });

  // Eager and lazy modes own their loaded state: the SSR'd (or lazily-fetched) <img> may finish
  // loading BEFORE hydration, in which case React attaches onLoad too late to ever fire — the
  // effect below catches that by reading `complete` off the live element. Ref + effect, not
  // a render-time read: reading layout state during render would break hydration determinism.
  const isOwnSharpLoadMode = isEagerSharpImage || isLazySharpImage;
  // Keyed by URL, not a boolean: a photoUrl swap (editor live preview) must read as "not loaded"
  // DURING the very render that shows the new URL. A boolean reset in an effect leaves one frame
  // where the blur sits at opacity 0 over the not-yet-loaded new image, then visibly animates
  // back in — deriving from `loadedOwnSharpUrl === photoUrl` invalidates synchronously instead.
  const [loadedOwnSharpUrl, setLoadedOwnSharpUrl] = useState<string | null>(null);
  const isOwnSharpLoaded = loadedOwnSharpUrl === photoUrl;
  const ownSharpImgRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!isOwnSharpLoadMode) return;
    // Catches images that finished loading BEFORE hydration attached onLoad (React 17 never
    // fires onLoad for those) — and, after a photoUrl swap, re-marks synchronously-cached images.
    const img = ownSharpImgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setLoadedOwnSharpUrl(photoUrl);
    }
  }, [isOwnSharpLoadMode, photoUrl]);

  // Hydration latch for the lazy sharp img's `sizes`: call sites may derive `width` from
  // window.innerWidth at render time (CoverPhoto's full-bleed branch does), which is 0 on the
  // real server but a real number during the browser's hydrating render — attaching sizes on
  // first render would make SSR markup and hydration disagree, and a pre-hydration lazy fetch
  // could then re-resolve to a second candidate when React patches the attribute. Both first
  // renders therefore omit sizes (browser default 100vw — a bounded over-fetch, only if the
  // user reaches the image before hydration); the measured value applies post-mount.
  const [isSizesLatchOpen, setIsSizesLatchOpen] = useState(false);
  useEffect(() => {
    if (isLazySharpImage) setIsSizesLatchOpen(true);
  }, [isLazySharpImage]);

  // Crop-scaled static estimate — the parse-time fetch this ships to the browser BEFORE any
  // measurement exists. This must already be the final, sharp candidate: at DPR 3, Chrome caps
  // an upscaled image's LCP candidate size at its own natural (fetched) size, so if this shipped
  // the un-scaled per-layout guess and the later measured takeover (below) then upgraded the
  // srcset pick (e.g. w1280 → w2048), the sharper repaint would register as a LARGER LCP
  // candidate and re-fire LCP at hydration+fetch time — worse than the legacy hydration-gated
  // path (see doc/improve-guest-site-perf/18-guest-lcp-p80-under-2s/README.md for measured
  // traces). Scaling the static estimate by the same cropScaleFactor the actual <img> renders at
  // (see effectiveContainerWidthPx above — SAME factor by construction, not recomputed) means the
  // measured takeover normally resolves to the same srcset bucket, so it's a no-op in the common
  // case and only corrects for real drift (resize, orientation change) — never a first-paint
  // upgrade. Do NOT simplify this back to the unscaled `props.ssrSizes` — that reintroduces the
  // LCP re-fire.
  // Justified memo: inputs are stable across a photo's lifetime, the work is string parsing, and
  // this component re-renders on every progressive-load transition.
  const cropScaledStaticSizes = useMemo(() => (props.ssrSizes ? scaleSizesExpression(props.ssrSizes, cropScaleFactor) : props.ssrSizes), [props.ssrSizes, cropScaleFactor]);

  const eagerSharp: EagerSharp | undefined = isEagerSharpImage
    ? {
        src: highResUrl,
        srcSet: highResSrcSet,
        // containerWidthPx is 0 during SSR and the very first client render, so highResSizes
        // stays undefined then and this falls back to cropScaledStaticSizes (SSR markup and
        // hydration agree, no mismatch). Once useSizeObserver measures the real container,
        // highResSizes reports the DPR-capped crop-scaled width and takes over — see
        // cropScaledStaticSizes's comment above for why the static fallback must already be
        // crop-scaled rather than a plain per-layout guess.
        sizes: highResSizes ?? cropScaledStaticSizes,
        imgRef: ownSharpImgRef,
        onLoad: () => setLoadedOwnSharpUrl(photoUrl),
      }
    : undefined;

  const lazySharp: LazySharp | undefined = isLazySharpImage
    ? {
        src: highResUrl,
        srcSet: highResSrcSet,
        // Gated behind the post-mount latch (see isSizesLatchOpen above) — no static fallback
        // like eagerSharp's ssrSizes; pre-latch the browser's 100vw default is the bounded
        // worst case.
        sizes: isSizesLatchOpen ? highResSizes : undefined,
        imgRef: ownSharpImgRef,
        onLoad: () => setLoadedOwnSharpUrl(photoUrl),
      }
    : undefined;

  // In eager/lazy modes, isHighResLoaded from useProgressiveImage stays permanently false
  // (skipPreload mode performs no offscreen preload) — effectiveIsLoaded substitutes the
  // component-owned state so the sharp layer's attributes and the blur layer's crossfade stay
  // in sync.
  const effectiveIsLoaded = isOwnSharpLoadMode ? isOwnSharpLoaded : isHighResLoaded;
  // Eager/lazy modes always play the full crossfade (the image arrives from network mid-paint;
  // "instant" cache styling would flash).
  const effectiveIsFromCache = isOwnSharpLoadMode ? false : isHighResFromCache;

  // String value — primitive, comparable by value; no useMemo needed (the inline-style objects
  // at each leaf are reconstructed every render regardless).
  const crossfadeTransition = buildCrossfadeTransition(crossfadeDelayMs);

  const [useCanvasFallback, setUseCanvasFallback] = useState(false);

  const handleHighResError = () => {
    setUseCanvasFallback(true);
  };

  // Canvas fallback for cropped fixed-aspect/square when CSS <img> fails
  if (useCanvasFallback && cropInfo) {
    return (
      <AnimatedPhotoBox
        skipAnimation={skipAnimation}
        width="100%"
        height="100%"
        style={{ isolation: 'isolate' }}
      >
        <CanvasFallback
          imageUrl={effectiveIsLoaded ? highResUrl : lowResUrl}
          cropInfo={cropInfo}
          width={width}
          height={height}
        />
      </AnimatedPhotoBox>
    );
  }

  const renderPhoto = () => {
    if (!cropInfo) {
      return (
        <UncroppedPhoto
          lowResUrl={lowResUrl}
          highResUrl={highResUrl}
          isHighResLoaded={effectiveIsLoaded}
          isHighResFromCache={effectiveIsFromCache}
          backgroundPosition={backgroundPosition}
          objectFit={objectFit}
          crossfadeTransition={crossfadeTransition}
          highResSrcSet={highResSrcSet}
          highResSizes={highResSizes}
          lazySharp={lazySharp}
        />
      );
    }

    switch (containerType) {
      case 'viewport':
        return (
          <CroppedViewport
            lowResUrl={lowResUrl}
            highResUrl={highResUrl}
            isHighResLoaded={isHighResLoaded}
            isHighResFromCache={isHighResFromCache}
            cropInfo={cropInfo}
            containerAspectRatio={containerAspectRatio!}
            crossfadeTransition={crossfadeTransition}
          />
        );
      case 'square':
        return (
          <CroppedSquare
            lowResUrl={lowResUrl}
            highResUrl={highResUrl}
            isHighResLoaded={effectiveIsLoaded}
            isHighResFromCache={effectiveIsFromCache}
            cropInfo={cropInfo}
            onHighResError={handleHighResError}
            crossfadeTransition={crossfadeTransition}
            highResSrcSet={highResSrcSet}
            highResSizes={highResSizes}
            eagerSharp={eagerSharp}
          />
        );
      case 'fixed-aspect':
      default:
        return (
          <CroppedFixedAspect
            lowResUrl={lowResUrl}
            highResUrl={highResUrl}
            isHighResLoaded={effectiveIsLoaded}
            isHighResFromCache={effectiveIsFromCache}
            cropInfo={cropInfo}
            onHighResError={handleHighResError}
            crossfadeTransition={crossfadeTransition}
            highResSrcSet={highResSrcSet}
            highResSizes={highResSizes}
            eagerSharp={eagerSharp}
          />
        );
    }
  };

  return (
    <AnimatedPhotoBox
      skipAnimation={skipAnimation}
      width="100%"
      height="100%"
      style={{ isolation: 'isolate' }}
    >
      {renderPhoto()}
    </AnimatedPhotoBox>
  );
};

OptimizedEventGuestSitePhoto.displayName = 'OptimizedEventGuestSitePhoto';
